/**
 * Password panel of the web login page: credential fields, the step-up advisory
 * and the submit control.
 *
 * Presentational only. It owns no sign-in state and decides nothing about the
 * step-up - which flow owns the controller, and where a completed sign-in
 * redirects, stay with the caller, so this panel stays usable from any host that
 * can supply the form and the handlers.
 */

import { Loading01 } from '@untitled-ui/icons-react';
import { useTranslation } from 'react-i18next';

import {
  AUTH_BUTTON_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_LINK_CLASS,
} from '@/components/auth/auth-styles';
import { MfaCheckFailedNotice } from '@/components/auth/mfa-check-failed-notice';
import { PasswordField } from '@/components/auth/password-field';
import {
  authBrandOptionClass,
  entryFieldClass,
  entryLabelClass,
  entryLinkClass,
} from '@/components/brand/entry-styles';
import { AuthTextField } from '@/components/auth/auth-text-field';
import { Checkbox } from '@/components/base/checkbox/checkbox';
import { FormField, HookForm } from '@/components/base/form/hook-form';
import { Button } from '@/components/base/buttons/button';

import type { LoginFormData } from '@/components/auth/login-form-schema';
import type { PasswordSignInNotice } from '@/components/auth/use-password-sign-in';
import type { UseFormReturn } from 'react-hook-form';

interface LoginPasswordFormProps {
  readonly form: UseFormReturn<LoginFormData>;
  /** Desktop drops the remember-me control: those sessions are always persistent. */
  readonly desktop: boolean;
  /** True while ANY sign-in path is in flight, so the form cannot be raced. */
  readonly isDisabled: boolean;
  readonly isLoading: boolean;
  readonly rememberMe: boolean;
  /** Advisory state for a step-up check that could not answer. */
  readonly notice: PasswordSignInNotice;
  readonly onSubmit: (values: LoginFormData) => Promise<void>;
  /** Re-runs the step-up check behind a `check_failed` notice. */
  readonly onRetry: () => void;
  readonly onResetPassword: () => void;
}

export function LoginPasswordForm({
  form,
  desktop,
  isDisabled,
  isLoading,
  rememberMe,
  notice,
  onSubmit,
  onRetry,
  onResetPassword,
}: LoginPasswordFormProps) {
  const { t } = useTranslation('auth');
  // The desktop shell renders this form on the entry canvas, where the web auth
  // field styles cannot be reused: they are built for a white card on cool grey
  // and carry theme-flipping colours, while that canvas is the same cream in
  // both themes. See `@/components/brand/entry-styles`.
  const chrome = desktop
    ? {
        input: entryFieldClass,
        label: entryLabelClass,
        // Brand purple, matching the web login's own primary rather than the
        // entry sheet's ink pill: this is the submit, and it is the only filled
        // control in the desktop column - the social methods are outlined.
        submit: authBrandOptionClass,
        link: entryLinkClass,
        notice: 'not-empty:mb-4 text-center text-xs text-entry-ink/55',
      }
    : {
        input: AUTH_INPUT_CLASS,
        label: AUTH_LABEL_CLASS,
        submit: AUTH_BUTTON_CLASS,
        link: AUTH_LINK_CLASS,
        notice: 'not-empty:mb-4 text-center text-xs text-quaternary',
      };

  return (
    <HookForm
      className="flex flex-col gap-4"
      form={form}
      onSubmit={(event) => {
        if (isDisabled) {
          event.preventDefault();
          return;
        }
        void (async () => {
          try {
            await form.handleSubmit(onSubmit)(event);
          } catch {
            /* React Hook Form owns validation errors. */
          }
        })();
      }}
    >
      <FormField control={form.control} name="email">
        {({ field, fieldState }) => (
          <AuthTextField
            {...field}
            autoComplete="email"
            hint={fieldState.error?.message}
            inputClassName={chrome.input}
            isDisabled={isDisabled}
            isInvalid={fieldState.invalid}
            label={t('login.emailLabel')}
            labelClassName={chrome.label}
            placeholder={t('login.emailPlaceholder')}
            type="email"
          />
        )}
      </FormField>

      <PasswordField
        control={form.control}
        inputClassName={`${chrome.input} pr-10`}
        isDisabled={isDisabled}
        labelClassName={chrome.label}
        name="password"
      />

      {/* Notice and submit share a wrapper so they are ONE flex item: an empty
            live region then costs no `gap-4` of its own, which is what lets it
            stay mounted. Carries NO `empty:hidden` - `display: none` takes the
            region out of the accessibility tree, so the first failure would
            arrive as a node the screen reader has never seen and is not
            reliably announced. Same shape as the native sheet's
            `authInlineErrorClass`. */}
      <div>
        <MfaCheckFailedNotice
          className={chrome.notice}
          show={notice === 'check_failed'}
          onRetry={onRetry}
        />

        <Button className={chrome.submit} isDisabled={isDisabled} size="md" type="submit">
          {isLoading ? (
            <>
              <Loading01 aria-hidden="true" className="mr-2 size-4 animate-spin" />
              {t('login.signingIn')}
            </>
          ) : (
            t('login.signIn')
          )}
        </Button>
      </div>

      {/* Centred on desktop: with no remember-me control beside it the reset
            link is the row's only occupant, and flushing a lone item right
            leaves it hanging off the edge of a column that centres everything
            else. The web row still spreads, because it has two children. */}
      <div
        className={
          desktop ? 'flex items-center justify-center' : 'flex items-center justify-between'
        }
      >
        {!desktop && (
          <Checkbox
            isDisabled={isDisabled}
            isSelected={rememberMe}
            label={t('login.rememberMe')}
            size="sm"
            onChange={(isSelected) => {
              if (isDisabled) {
                return;
              }
              form.setValue('rememberMe', isSelected);
            }}
          />
        )}
        <Button
          className={chrome.link}
          color="link-color"
          isDisabled={isDisabled}
          size="md"
          type="button"
          onClick={onResetPassword}
        >
          {t('login.forgotPassword')}
        </Button>
      </div>
    </HookForm>
  );
}
